import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { collection, getDocs, onSnapshot, orderBy, query, documentId } from "firebase/firestore";

import { db } from "../../firebase";
import SevenInchesPage from "../SmartHMI/SevenInchesPage";
import { slugify } from "../../slugify";
import { productAltBase } from "../../utils/imageAlt";
import "./TFTDisplayPage.css";

const FIELD_PATTERNS = {
  title: [/^product.?name$/i, /^tit+le$/i, /^name$/i],
  partNo: [/part.?no/i, /part.?number/i, /model.?no/i, /biba.?part/i],
  displaySize: [/display.?size/i, /panel.?size$/i, /^size$/i],
  resolution: [/resolution/i],
  brightness: [/brightness/i, /nits?/i],
  interfaceType: [/interface/i],
  controller: [/controller/i, /driver.?ic/i],
  touchPanel: [/touch/i],
  price: [/^price$/i, /selling.?price/i, /^mrp$/i, /^rate$/i],
  stockPrice: [/stock.?price/i, /stock.?rate/i, /bulk.?price/i],
  stock: [/^stock$/i, /^qty$/i, /quantity/i, /stock.?(qty|quantity|count)/i, /availability/i],
  image: [/image/i, /photo/i, /picture/i],
  frontImage: [/front.*image/i],
  backImage: [/back.*image/i],
  galleryImage: [/gallery.*image/i],
  description: [/^description$/i, /^desc$/i, /^about$/i, /^summary$/i, /^details$/i],
  datasheetUrl: [/datasheet/i, /^dwg$/i, /drawing/i, /spec.?sheet/i, /\.pdf/i],
  video: [/^video$/i, /youtube/i, /video.?url/i],
  moq: [/^moq$/i],
leadTime: [/^lead.?time$/i],
};

const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));

// Some products have both a "title" and a "Title" field (a data-entry
// artifact — the same value typed twice under different casing, or one
// left blank after a rename). Both match the title pattern, and object key
// order from Firestore's map fields isn't guaranteed stable across reads,
// so picking the first match arbitrarily flips between the empty one and
// the real one on different loads. Prefer whichever match actually has a
// value over an empty one, regardless of which order they come back in.
const matchField = (fieldsObj, patterns) => {
  const entries = Object.entries(fieldsObj || {}).filter(([key]) => testKey(key, patterns));
  if (entries.length === 0) return "";
  const nonEmpty = entries.find(([, value]) => value !== "" && value != null);
  return (nonEmpty || entries[0])[1] ?? "";
};

const DEFAULT_STOCK = 50;

const TFTDetailPage = () => {
  const { categorySlug, subCategorySlug, partNoSlug } = useParams();

  const [rowDocRef, setRowDocRef] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Field names (from the subcategory's "fields" schema collection) whose
  // admin-defined _AddinDescription flag is true — those fields' values
  // show in the description area instead of the Specifications table.
  const [descriptionFieldNames, setDescriptionFieldNames] = useState(new Set());
  const [fieldMeta, setFieldMeta] = useState({});

  // STEP 1 — resolve the slug chain
  useEffect(() => {
    let isCancelled = false;
    setLoading(true);
    setNotFound(false);
    setRowDocRef(null);
    setRowData(null);

    const locate = async () => {
      try {
        if (!categorySlug || !subCategorySlug || !partNoSlug) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const categorySnap = await getDocs(collection(db, "product_categories"));
        let categoryDoc = null;
        categorySnap.forEach((d) => {
          if (slugify(d.data().categoryName) === categorySlug) categoryDoc = d;
        });
        if (!categoryDoc) {
          if (!isCancelled) { setNotFound(true); setLoading(false); }
          return;
        }

        const subCategorySnap = await getDocs(
          collection(db, "product_categories", categoryDoc.id, "subCategories")
        );
        let subCategoryDoc = null;
        subCategorySnap.forEach((d) => {
          const name = d.data().subCategoryName || d.id;
          if (slugify(name) === subCategorySlug) subCategoryDoc = d;
        });
        if (!subCategoryDoc) {
          if (!isCancelled) { setNotFound(true); setLoading(false); }
          return;
        }

        const subCategoryNameForMatch =
          subCategoryDoc.data()?.subCategoryName || subCategoryDoc.id;

        // Field schema for this subcategory — defines which field names
        // should surface in the description area (_AddinDescription: true)
        // versus staying in the Specifications table only.
        const fieldsSchemaSnap = await getDocs(
          collection(
            db, "product_categories", categoryDoc.id,
            "subCategories", subCategoryDoc.id, "fields"
          )
        );
        const descFieldNames = new Set();
const fieldMeta = {};

fieldsSchemaSnap.forEach((d) => {
    const schema = d.data();

    if (schema.fieldName) {
        const fieldName = String(schema.fieldName).trim();

        fieldMeta[fieldName] = {
            specGroup: schema.specGroup || "Specifications",
            displayOrder: schema.displayOrder ?? 999,
        };

        if (schema._AddinDescription === true) {
            descFieldNames.add(fieldName.toLowerCase());
        }
    }
});
setFieldMeta(fieldMeta);
        // Ordered explicitly by document ID — a plain, unordered
        // collection() query has no guaranteed stable order across
        // separate fetches, which would make the duplicate-suffix slug
        // below drift between page loads (works right after an in-app
        // navigation, then "Product not found" on a fresh refresh once
        // Firestore happens to return this subcategory's rows in a
        // different order). Every row-fetching call across this file,
        // ProductListingPage, and FrequentlyBoughtTogether must use this
        // same explicit order for their slugs to ever agree.
        const productDataSnap = await getDocs(
          query(
            collection(
              db, "product_categories", categoryDoc.id,
              "subCategories", subCategoryDoc.id, "productData"
            ),
            orderBy(documentId())
          )
        );
        // Rows are located by their Title slug now (not part number). Must
        // replicate ProductListingPage's exact same duplicate-handling
        // (first occurrence keeps the clean slug, later duplicates get the
        // doc id appended) over the same Firestore-ordered snapshot, or a
        // product sharing its Title with a sibling would resolve to the
        // wrong row.
        let foundRow = null;
        const usedTitleSlugs = new Set();
        productDataSnap.forEach((d) => {
          const fields = d.data().fields || {};
          const rowTitle = matchField(fields, FIELD_PATTERNS.title) || subCategoryNameForMatch;

          let rowSlug = slugify(rowTitle);
          if (usedTitleSlugs.has(rowSlug)) {
            rowSlug = `${rowSlug}-${d.id.slice(0, 6).toLowerCase()}`;
          }
          usedTitleSlugs.add(rowSlug);

          if (rowSlug === partNoSlug) foundRow = d;
        });
        console.log("[DEBUG] productDataSnap size:", productDataSnap.size, "looking for:", partNoSlug, "computed slugs:", JSON.stringify([...usedTitleSlugs]));
        if (!foundRow) {
          if (!isCancelled) { setNotFound(true); setLoading(false); }
          return;
        }

        if (isCancelled) return;

        setCategoryName(categoryDoc.data()?.categoryName || "");
        setSubCategoryName(subCategoryDoc.data()?.subCategoryName || "");
        setDescriptionFieldNames(descFieldNames);
        setRowDocRef(foundRow.ref);
      } catch (err) {
        console.error(err);
        if (!isCancelled) setLoading(false);
      }
    };

    locate();

    return () => {
      isCancelled = true;
    };
  }, [categorySlug, subCategorySlug, partNoSlug]);

  // STEP 2 — listen to row updates
  useEffect(() => {
    if (!rowDocRef) return;

    const unsub = onSnapshot(
      rowDocRef,
      (snap) => {
        if (!snap.exists()) {
          setNotFound(true);
          setRowData(null);
        } else {
          const fields = snap.data().fields || {};
          setRowData(fields);
          console.log("✅ Product data loaded:", fields);
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [rowDocRef]);

  if (loading) {
    return (
      <div className="product-skeleton-page">
        <div className="product-skeleton-top">
          <div className="skel skel-image" />
          <div className="product-skeleton-info">
            <div className="skel skel-badge" />
            <div className="skel skel-title" />
            <div className="skel skel-title skel-title--short" />
            <div className="skel skel-price" />
            <div className="product-skeleton-cta">
              <div className="skel skel-btn" />
              <div className="skel skel-btn skel-btn--sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !rowData) {
    return (
      <div className="loading-state">
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <p>Product not found.</p>
      </div>
    );
  }

  const get = (key) => matchField(rowData, FIELD_PATTERNS[key]);

  const realPartNo = get("partNo") || decodeURIComponent(partNoSlug || "");
  const priceRaw = get("price");
  const stockRaw = get("stock");
  // ✅ Add these here
const moq = get("moq");
const leadTime = get("leadTime");
  const description = get("description");
  const realDatasheetUrl = get("datasheetUrl");
  const videoUrl = get("video");

  // No fallback price -- a blank/unparseable price means "not priced yet"
  // (see SevenInchesPage.jsx, which renders "-" and disables Add to Cart
  // for a null price rather than ever charging a fabricated number).
  const parsedPrice = parseFloat(priceRaw);
  const priceNum = priceRaw !== "" && !isNaN(parsedPrice) ? parsedPrice : null;

  const parsedStock = parseFloat(stockRaw);
  const stockNum = stockRaw !== "" && !isNaN(parsedStock) ? parsedStock : DEFAULT_STOCK;

  // ✅ EXTRACT PRICE TIERS
  // qty/maxQty stay numeric (not the display-string they used to be) so
  // SevenInchesPage.jsx can compare them against the selected order
  // quantity to pick the right slab price live.
  let priceTiers = null;
  try {
    if (rowData.priceTiers && Array.isArray(rowData.priceTiers)) {
      priceTiers = rowData.priceTiers
        .map(tier => ({
          qty: Number(tier.qty) || 0,
          maxQty: tier.maxQty !== null && tier.maxQty !== undefined ? Number(tier.maxQty) : null,
          price: parseFloat(tier.price) || 0,
        }))
        .filter(tier => tier.qty > 0 && tier.price > 0);

      console.log("✅ Price tiers found:", priceTiers);
    }
  } catch (err) {
    console.warn("⚠️ Error parsing priceTiers:", err);
    priceTiers = null;
  }

  // ✅ EXTRACT FREQUENTLY BOUGHT TOGETHER
  let frequentlyBoughtTogether = null;
  try {
    if (rowData.frequentlyBoughtTogether && Array.isArray(rowData.frequentlyBoughtTogether)) {
      frequentlyBoughtTogether = rowData.frequentlyBoughtTogether;
      console.log("✅ Frequently bought together found:", frequentlyBoughtTogether);
    }
  } catch (err) {
    console.warn("⚠️ Error parsing frequentlyBoughtTogether:", err);
    frequentlyBoughtTogether = null;
  }
let similarProducts = null;

try {
  if (
    rowData.similarProducts &&
    Array.isArray(rowData.similarProducts)
  ) {
    similarProducts = rowData.similarProducts;
    console.log(
      "✅ Similar products found:",
      similarProducts
    );
  }
} catch (err) {
  console.warn("Error parsing similarProducts:", err);
  similarProducts = null;
}

  // The item's tagged "Compatible Accessories and Related Items" (tixaru
  // Items page), synced verbatim -- title/image/price/route are already
  // fully resolved server-side, unlike similarProducts above.
  const relatedProducts = Array.isArray(rowData.relatedProducts) ? rowData.relatedProducts : null;

  // The remaining tixaru datasheet sections -- same synced-verbatim,
  // null-when-absent pattern as relatedProducts/priceTiers above, so
  // SevenInchesPage.jsx can hide each section independently rather than
  // rendering an empty table/image.
  const opticalCharacteristics = Array.isArray(rowData.opticalCharacteristics) ? rowData.opticalCharacteristics : null;
  const electricalAbsoluteMax = Array.isArray(rowData.electricalAbsoluteMax) ? rowData.electricalAbsoluteMax : null;
  const electricalDCCharacteristics = Array.isArray(rowData.electricalDCCharacteristics)
    ? rowData.electricalDCCharacteristics
    : null;
  const reliabilitySpecs = Array.isArray(rowData.reliabilitySpecs) ? rowData.reliabilitySpecs : null;
  const mechanicalSpecsImageUrl = rowData.mechanicalSpecsImageUrl || null;
  const mechanicalDrawingImageUrl = rowData.mechanicalDrawingImageUrl || null;
  // Block Diagram image (Items page -> Attributes tab, S.No. 7).
  const blockDiagramImageUrl = rowData.blockDiagramImageUrl || null;
  const videoLinks = Array.isArray(rowData.videoLinks) ? rowData.videoLinks : null;
  // Compliance/marketing badges (BIS Certified, Make in India, ...) from
  // the item's Product Tags section on tixaru.
  const tags = Array.isArray(rowData.tags) ? rowData.tags : null;
  // Free-text notes above the General Specs table (Items page -> Attributes
  // tab, S.No. 5).
  const generalSpecsNotes = rowData.generalSpecsNotes || null;

  // Fields the admin flagged with _AddinDescription: true (in the
  // subcategory's "fields" schema) surface here, in the description area,
  // instead of the Specifications table.
  const isDescriptionFlaggedKey = (key) =>
    descriptionFieldNames.has(key.trim().toLowerCase());

  const descriptionFromFlaggedFields = Object.entries(rowData)
    .filter(
      ([key, value]) =>
        isDescriptionFlaggedKey(key) &&
        value !== "" &&
        value != null &&
        key !== "priceTiers" &&
        key !== "frequentlyBoughtTogether"
    )
    .map(([key, value]) => `${key}: ${value}`)
    .join("\n");

  const splitUrls = (raw) =>
    String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const flattenValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.flatMap((item) => (typeof item === "string" ? splitUrls(item) : []));
    }
    if (typeof value === "string") return splitUrls(value);
    return [];
  };

  const entriesMatching = (pattern) =>
    Object.entries(rowData).filter(([key]) => testKey(key, pattern));

  const frontImageEntries = entriesMatching(FIELD_PATTERNS.frontImage);
  const backImageEntries = entriesMatching(FIELD_PATTERNS.backImage);
  const galleryImageEntries = entriesMatching(FIELD_PATTERNS.galleryImage);

  // Every image-shaped field (front, back, gallery, product photo, or any
  // other stray "photo"/"picture"-named field left over from earlier data
  // entry) is hidden from the Specifications table regardless of whether
  // its value is actually rendered anywhere -- but only Front Image Url /
  // Back Image Url / Gallery Images (tixaru's own fields, synced by
  // catalogSync.js) are ever shown as a photo. Anything else that merely
  // *looks* like an image field is deliberately ignored rather than shown
  // as a stray gallery photo, same reasoning as never fabricating a price.
  const imageEntries = Object.entries(rowData).filter(([key]) => testKey(key, FIELD_PATTERNS.image));

  const frontUrls = frontImageEntries.flatMap(([, v]) => flattenValue(v));
  const backUrls = backImageEntries.flatMap(([, v]) => flattenValue(v));
  const galleryUrls = galleryImageEntries.flatMap(([, v]) => flattenValue(v));

  // Main gallery (thumbnail strip at the top of the page): Tixaru's Front
  // Image and Back Image first, then every extra photo from its Gallery
  // Images field (Items page -> Product Images, third image onward) -- so
  // the scroller shows exactly as many photos as tixaru has for the item.
  // An empty array here means "Image Coming Soon" (see SevenInchesPage.jsx)
  // rather than a stock placeholder photo.
  const dedupedTopGalleryUrls = [...new Set([frontUrls[0], backUrls[0], ...galleryUrls].filter(Boolean))];

  const altBase = productAltBase({ partNo: realPartNo, typeName: subCategoryName || categoryName, getField: (k) => (rowData[k] != null ? String(rowData[k]) : "") });
  const images = dedupedTopGalleryUrls.map((src, i) => ({ src, alt: `${altBase} view ${i + 1}` }));

  // Banners used to also carry overflow gallery/product-photo images --
  // those now show in the main gallery scroller itself (see images above),
  // so these stay empty. Kept as props since SevenInchesPage.jsx already
  // handles an empty banner list gracefully (renders nothing).
  const bannersAboveSpecs = [];
  const bannersBelowSpecs = [];

  // Note: description-flagged fields (_AddinDescription: true) are
  // deliberately NOT added to usedKeys here — they should show in both the
  // description area above AND the Specifications table, not just one.
  const usedKeys = new Set([
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.title)),
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.partNo)),
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.price)),
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.stock)),
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.description)),
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.datasheetUrl)),
     ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.moq)),
    ...Object.keys(rowData).filter((k) => testKey(k, FIELD_PATTERNS.leadTime)),
    
    ...imageEntries.map(([k]) => k),
    "priceTiers",
    "frequentlyBoughtTogether",
    "similarProducts",
    "relatedProducts",
    "isFeatured",
    "video",
    "About product",
    "generalSpecs",
    "opticalCharacteristics",
    "electricalAbsoluteMax",
    "electricalDCCharacteristics",
    "reliabilitySpecs",
    "mechanicalSpecsImageUrl",
    "mechanicalDrawingImageUrl",
    "blockDiagramImageUrl",
    "videoLinks",
    "tags",
    "generalSpecsNotes",
  ]);

  // The item's full General Specs table (tixaru Items page -> Attributes
  // tab), synced verbatim as rowData.generalSpecs -- when present, this is
  // the actual source of truth for the product and takes over the
  // Specifications table entirely, in the same row order it was entered on
  // tixaru. Falls back to the old leftover-Firestore-fields heuristic only
  // for rows that predate this field / have nothing in General Specs yet.
  const specs =
    Array.isArray(rowData.generalSpecs) && rowData.generalSpecs.length > 0
      ? rowData.generalSpecs.map((row, i) => ({
          category: "Specifications",
          displayOrder: i,
          label: row.item,
          value: row.unit ? `${row.specification} ${row.unit}` : row.specification,
        }))
      : Object.entries(rowData)
          .filter(([key, value]) =>
              !usedKeys.has(key) &&
              value !== "" &&
              value != null
          )
          .map(([key, value]) => {

              const meta = fieldMeta[key] || {};

              return {
                  category: meta.specGroup || "Specifications",
                  displayOrder: meta.displayOrder ?? 999,
                  label: key,
                  value: String(value),
              };
          })
          .sort((a, b) => {

              if (a.category === b.category) {
                  return a.displayOrder - b.displayOrder;
              }

              return a.category.localeCompare(b.category);
          });

  const title = get("title") || subCategoryName || categoryName || "Product";
  const productDetails = rowData["About product"] || "";
  const subtitle = descriptionFromFlaggedFields || description || "";

  const pageData = {
    product: {
      badge: `Part No : ${realPartNo}`,
      badge1: "Make : BIBA ELECTRONICS",
      stock: stockNum,
      price: priceNum,
          moq,
    leadTime,
      stockPrice: null,
      title,
      subtitle,
      highlight: title,
      // No fallback path -- a product with nothing uploaded to "BIBA 2D PDF
      // File" (Items page -> Attachments) has no datasheet, not a fake one.
      // SevenInchesPage.jsx hides the PDF thumbnail/download/preview
      // entirely when this is empty, same reasoning as price/mechanical
      // specs never getting a fabricated placeholder either.
      datasheetUrl: realDatasheetUrl || null,
      mechanicalDrawingUrl: realDatasheetUrl || null,
    },
    videoUrl,
    images,
    bannersAboveSpecs,
    bannersBelowSpecs,
    productDetails,
    specs,

    pricing: undefined,
    applications: [],

    tabs: [
      { key: "specs", label: "Specifications", emoji: "" },
      { key: "resources", label: "Resources", emoji: "" },
    ],
    software: null,
    priceTiers: priceTiers,
    frequentlyBoughtTogether: frequentlyBoughtTogether,
    similarProducts: similarProducts,
    relatedProducts: relatedProducts,
    opticalCharacteristics,
    electricalAbsoluteMax,
    electricalDCCharacteristics,
    reliabilitySpecs,
    mechanicalSpecsImageUrl,
    mechanicalDrawingImageUrl,
    blockDiagramImageUrl,
    videoLinks,
    tags,
    generalSpecsNotes,
  };

  // Catalog product names are usually just the part number; lead with what it is.
  const displayName =
    realPartNo && String(title).trim().toLowerCase() === String(realPartNo).trim().toLowerCase() && (subCategoryName || categoryName)
      ? `${subCategoryName || categoryName} ${realPartNo}`
      : title;
  const metaTitle = `${displayName} | BIBA Electronics`;
  const metaDescriptionRaw =
    subtitle || description || productDetails || `${title} — ${categoryName || "Display"} from BIBA Electronics.`;
  const metaDescription = String(metaDescriptionRaw).replace(/\s+/g, " ").trim().slice(0, 160);

  const SITE_URL = "https://www.bibaelectronics.com";
  const pageUrl = `${SITE_URL}/tft/${categorySlug}/${subCategorySlug}/${partNoSlug}`;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: displayName,
    ...(realPartNo ? { sku: realPartNo, mpn: realPartNo } : {}),
    description: metaDescription,
    brand: { "@type": "Brand", name: "BIBA Electronics" },
    ...(images.length ? { image: images.slice(0, 4).map((im) => im.src) } : {}),
    url: pageUrl,
    // Offer only when the catalog has a real price; never a fabricated one.
    ...(priceNum != null
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: priceNum,
            url: pageUrl,
            availability:
              stockNum > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            seller: { "@type": "Organization", name: "BIBA Electronics Pvt Ltd" },
          },
        }
      : {}),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      ...(categoryName && subCategoryName
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: subCategoryName,
              item: `${SITE_URL}/category/${categorySlug}/${subCategorySlug}`,
            },
          ]
        : []),
      { "@type": "ListItem", position: categoryName && subCategoryName ? 3 : 2, name: displayName, item: pageUrl },
    ],
  };

  return (
    <div className="tft-detail-page-wrapper">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {images[0]?.src && <meta property="og:image" content={images[0].src} />}
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {images[0]?.src && <meta name="twitter:image" content={images[0].src} />}
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <SevenInchesPage
        pageData={pageData}
        showQuantitySelector={true}
        showAddToCart={true}
        showPrice={true}
      />
    </div>
  );
};

export default TFTDetailPage;
